/* File: frontend/src/pages/ClientProfilePage.js */
/* Version: 1.4 (quick-260915-d2d — инлайновый цвет убран, экран на общем слое) */
/* Description: Fixed empty "Active Cases" — API returns `latest_case`, not `cases`. Read the field that actually exists. */

import React, { useState, useEffect } from 'react';
import HistoryTimeline from '../components/history/HistoryTimeline';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import InputMask from 'react-input-mask'; // Не забудьте, что это нужно для модалки
import { toast } from 'react-hot-toast';
import { 
    FiMail, FiPhone, FiMapPin, FiBriefcase, 
    FiClock, FiFileText, FiArrowLeft, FiEdit, FiAlertCircle, FiX 
} from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
// 🔴 ОБЩИЙ СЛОЙ ОФОРМЛЕНИЯ: `Sostoyanie` — единственный держатель того, как
//    экран говорит «не нашли» и «пусто».
import { Sostoyanie } from '../components/FitenkoUzor';
import './ClientProfilePage.css';

// --- SUB-COMPONENT: Edit Modal (Встроено локально для надежности) ---
const ClientEditModal = ({ isOpen, onClose, clientData, onUpdate }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [timezone, setTimezone] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Заполняем форму данными при открытии
    useEffect(() => {
        if (isOpen && clientData) {
            setFirstName(clientData.first_name || '');
            setLastName(clientData.last_name || '');
            setEmail(clientData.email || '');
            setPhone(clientData.phone_number || '');
            setAddress(clientData.address || '');
            setTimezone(clientData.timezone || 'America/New_York');
        }
    }, [isOpen, clientData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const cleanPhone = phone ? phone.replace(/[^\d+]/g, '') : null;
            const updatePayload = {
                first_name: firstName,
                last_name: lastName,
                email: email,
                phone_number: cleanPhone,
                address: address,
                timezone: timezone
            };
            await onUpdate(clientData.id, updatePayload);
            onClose();
        } catch (error) {
            console.error("Update failed", error);
            // Ошибку покажет родительский компонент или тост
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
                <button onClick={onClose} className="modal-close-button"><FiX /></button>
                <h2 style={{marginTop:0, marginBottom: '20px'}}>Edit Client Profile</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group-row" style={{display:'flex', gap:'16px'}}>
                        <div className="form-group" style={{flex:1}}>
                            <label style={{display:'block', marginBottom:'5px', fontWeight:600}}>First Name</label>
                            <input type="text" className="form-input cpf-field" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                        </div>
                        <div className="form-group" style={{flex:1}}>
                            <label style={{display:'block', marginBottom:'5px', fontWeight:600}}>Last Name</label>
                            <input type="text" className="form-input cpf-field" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        </div>
                    </div>

                    <div className="form-group" style={{marginTop:'15px'}}>
                        <label style={{display:'block', marginBottom:'5px', fontWeight:600}}>Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="cpf-field" />
                    </div>

                    <div className="form-group" style={{marginTop:'15px'}}>
                        <label style={{display:'block', marginBottom:'5px', fontWeight:600}}>Phone</label>
                        <InputMask mask="+1 (999) 999-9999" value={phone} onChange={(e) => setPhone(e.target.value)}>
                            {(inputProps) => <input {...inputProps} type="tel" className="cpf-field" />}
                        </InputMask>
                    </div>

                    <div className="form-group" style={{marginTop:'15px'}}>
                        <label style={{display:'block', marginBottom:'5px', fontWeight:600}}>Address</label>
                        <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Full Address" className="cpf-field" />
                    </div>

                    <div className="modal-actions cpf-modal-actions" style={{display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'25px', paddingTop:'20px'}}>
                         <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                        <button type="submit" className="btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const ClientProfilePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Состояние для модального окна
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    // История клиента грузится отдельным запросом: она может быть длинной, и
    // её отказ не должен мешать показу самой карточки.
    const [history, setHistory] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [historyError, setHistoryError] = useState(null);

    // Функция загрузки данных (вынесена, чтобы вызывать после обновления)
    const fetchClientData = async () => {
        try {
            // Проверка на наличие метода в apiService
            if (!apiService.getUser) {
               console.warn("apiService.getUser is missing, using fallback?");
            }
            const data = await apiService.getUser(id);
            if (!data) throw new Error("Empty data");
            setClient(data);
        } catch (err) {
            console.error("Error fetching client:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClientData();
    }, [id]);

    useEffect(() => {
        let cancelled = false;
        setHistoryLoading(true);
        setHistoryError(null);
        apiService.getClientHistory(id)
            .then((data) => { if (!cancelled) setHistory(data); })
            .catch((e) => { if (!cancelled) setHistoryError(e.message); })
            .finally(() => { if (!cancelled) setHistoryLoading(false); });
        return () => { cancelled = true; };
    }, [id]);

    // Обработчик обновления клиента
    const handleUpdateClient = async (userId, data) => {
        try {
            await apiService.updateUser(userId, data);
            toast.success("Client profile updated successfully");
            await fetchClientData(); // Обновляем данные на странице
        } catch (error) {
            toast.error("Failed to update profile: " + error.message);
        }
    };

    // Обработчик кнопки Message
    const handleMessageClick = () => {
        if (client && client.email) {
            window.location.href = `mailto:${client.email}`;
        } else {
            toast.error("Client has no email address");
        }
    };

    if (loading) return <LoadingSpinner message="Loading client dossier..." />;
    
    if (error) {
        return (
            <div className="error-state cpf-error" style={{flexDirection: 'column', gap: '10px'}}>
                <FiAlertCircle size={40} />
                <h3>Error Loading Profile</h3>
                <p>Reason: {error}</p>
                <button className="btn-secondary" onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }
    
    if (!client) return <div className="error-state"><Sostoyanie tekst={`Client not found (ID: ${id}).`} /></div>;

    // API delivers the client's case as `latest_case` (see /users/clients-with-details → UserWithCaseInfo).
    // Prefer a full `cases` array if a future endpoint provides one; otherwise wrap latest_case.
    const activeCases = (client.cases && client.cases.length)
        ? client.cases
        : (client.latest_case ? [client.latest_case] : []);

    return (
        <div className="client-profile-container">
            {/* --- TOP BAR --- */}
            <div className="profile-top-bar">
                <button className="btn-back" onClick={() => navigate('/user-management')}>
                    <FiArrowLeft /> Back to Clients
                </button>
                <div className="profile-actions">
                    {/* КНОПКА MESSAGE */}
                    <button className="btn-secondary" onClick={handleMessageClick}>
                        Message
                    </button>
                    
                    {/* КНОПКА EDIT PROFILE */}
                    <button className="btn-primary" onClick={() => setIsEditModalOpen(true)}>
                        <FiEdit /> Edit Profile
                    </button>
                </div>
            </div>

            <div className="profile-layout">
                {/* --- SIDEBAR --- */}
                <aside className="profile-sidebar">
                    <div className="client-card-platinum">
                        <div className="client-avatar-large">
                            {client.first_name?.[0]}{client.last_name?.[0]}
                        </div>
                        <h1 className="client-name-title">{client.first_name} {client.last_name}</h1>
                        <span className="client-role-badge">Client</span>

                        <div className="contact-list">
                            <div className="contact-item">
                                <FiMail className="c-icon" />
                                <span>{client.email}</span>
                            </div>
                            <div className="contact-item">
                                <FiPhone className="c-icon" />
                                <span>{client.phone_number || 'No phone'}</span>
                            </div>
                            <div className="contact-item">
                                <FiMapPin className="c-icon" />
                                <span>{client.address || 'Address not set'}</span>
                            </div>
                            <div className="contact-item">
                                <FiClock className="c-icon" />
                                <span>{client.timezone || 'America/New_York'}</span>
                            </div>
                        </div>
                    </div>
                </aside>

                {/* --- MAIN CONTENT --- */}
                <main className="profile-content">
                    <section className="content-section">
                        <div className="section-header">
                            <h2><FiBriefcase /> Active Cases</h2>
                        </div>
                        <div className="platinum-table-container">
                            <table className="platinum-table">
                                <thead>
                                    <tr>
                                        <th>Case Title</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeCases.length > 0 ? activeCases.map(c => (
                                        <tr key={c.id}>
                                            <td className="fw-600">{c.title}</td>
                                            <td><span className={`status-pill ${c.status?.toLowerCase()}`}>{c.status}</span></td>
                                            <td>
                                                <button className="btn-link" onClick={() => navigate(`/cases/${c.id}`)}>View</button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="3" className="cpf-empty-cell"><Sostoyanie tekst="No active cases found" /></td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <HistoryTimeline
                        history={history}
                        loading={historyLoading}
                        error={historyError}
                    />
                </main>
            </div>

            {/* --- МОДАЛЬНОЕ ОКНО --- */}
            <ClientEditModal 
                isOpen={isEditModalOpen} 
                onClose={() => setIsEditModalOpen(false)} 
                clientData={client} 
                onUpdate={handleUpdateClient} 
            />
        </div>
    );
};

export default ClientProfilePage;